import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PublicSharedDeck — read-only presentation viewer at /s/:slug
 * - No auth required. Records a view event server-side when loaded.
 * - Injects OG meta tags for rich social previews (works for JS-capable crawlers).
 * - Password gate if the share is protected.
 * - Handles expiry (HTTP 410) with a friendly message.
 */
export default function PublicSharedDeck() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [unlockToken, setUnlockToken] = useState(() => sessionStorage.getItem(`share_tok_${slug}`) || "");
  const [unlocking, setUnlocking] = useState(false);
  const [index, setIndex] = useState(0);
  const containerRef = useRef(null);

  // Inject OG + title meta tags whenever we get metadata.
  useEffect(() => {
    if (!meta) return;
    document.title = `${meta.title} · SlideCon`;
    const setMetaTag = (attr, key, value) => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", value);
    };
    const origin = window.location.origin;
    const abs = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL || origin}${u}`);
    setMetaTag("property", "og:title", meta.title);
    setMetaTag("property", "og:description", meta.description || "");
    setMetaTag("property", "og:image", abs(meta.og_image_url));
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:url", window.location.href);
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", meta.title);
    setMetaTag("name", "twitter:description", meta.description || "");
    setMetaTag("name", "twitter:image", abs(meta.og_image_url));
    setMetaTag("name", "description", meta.description || "");
  }, [meta]);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      try {
        const m = await axios.get(`${API}/public/share/${slug}/meta`);
        setMeta(m.data);
      } catch (err) {
        if (err?.response?.status === 410) {
          setError("This share link has expired.");
          return;
        }
        // meta 404 is fine — we'll fall through to the main GET and error there.
      }

      try {
        const params = unlockToken ? { t: unlockToken } : {};
        const res = await axios.get(`${API}/public/share/${slug}`, { params });
        setData(res.data);
        setNeedsPassword(false);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401 && detail === "password_required") {
          setNeedsPassword(true);
        } else if (status === 410) {
          setError("This share link has expired.");
        } else {
          setError(detail || "This share link isn't active.");
        }
      }
    };
    load();
  }, [slug, unlockToken]);

  useEffect(() => {
    const handler = (e) => {
      if (!data?.project?.slides) return;
      const max = data.project.slides.length - 1;
      if (e.key === "ArrowRight" || e.key === " ") setIndex((i) => Math.min(i + 1, max));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
      if (e.key === "f") containerRef.current?.requestFullscreen?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [data]);

  const submitPassword = async (e) => {
    e?.preventDefault?.();
    setUnlocking(true);
    try {
      const res = await axios.post(`${API}/public/share/${slug}/unlock`, { password: passwordInput });
      const tok = res.data?.token;
      if (tok) {
        sessionStorage.setItem(`share_tok_${slug}`, tok);
        setUnlockToken(tok);
      }
    } catch (err) {
      setError(null);
      // stay on the password gate; show a soft error inline
      const detail = err?.response?.data?.detail || "Wrong password";
      setPasswordInput("");
      // eslint-disable-next-line no-alert
      window.alert(detail);
    } finally {
      setUnlocking(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-semibold text-slate-100 mb-2">Link not available</h1>
          <p className="text-slate-400">{error}</p>
          <a href="/" className="mt-6 inline-block text-amber-400 hover:text-amber-300 text-sm">← Back to SlideCon</a>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
        <form onSubmit={submitPassword} className="w-full max-w-sm text-center" data-testid="share-password-gate">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-semibold text-slate-100 mb-2">{meta?.title || "Password required"}</h1>
          <p className="text-slate-400 mb-6">The owner has protected this share link.</p>
          <input
            type="password"
            className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-amber-500"
            placeholder="Enter password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            autoFocus
            data-testid="share-password-gate-input"
          />
          <button
            type="submit"
            disabled={unlocking || !passwordInput}
            className="mt-4 w-full py-3 rounded-lg bg-amber-500 text-black font-semibold hover:bg-amber-400 disabled:opacity-50"
            data-testid="share-password-gate-submit"
          >
            {unlocking ? "Checking…" : "Unlock"}
          </button>
        </form>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400 animate-pulse">Loading…</div>
      </div>
    );
  }

  const slides = data.project?.slides || [];
  const current = slides[index] || {};
  const total = slides.length;

  return (
    <div ref={containerRef} className="min-h-screen bg-black flex flex-col" data-testid="public-shared-deck">
      <header className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur px-6 py-3 flex items-center justify-between">
        <div className="text-slate-300 text-sm">
          <span className="font-medium text-slate-100">{data.project?.name || "Shared deck"}</span>
          <span className="text-slate-500 mx-2">·</span>
          <span className="text-slate-500">shared via SlideCon</span>
        </div>
        <div className="text-slate-500 text-xs">{index + 1} / {total}</div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6" onClick={() => setIndex((i) => Math.min(i + 1, total - 1))}>
        {total === 0 ? (
          <div className="text-slate-500">This deck is empty.</div>
        ) : (
          <div
            className="w-full max-w-5xl aspect-video rounded-lg shadow-2xl flex items-center justify-center px-12 py-8 text-center"
            style={{
              background: current.background_color || "linear-gradient(135deg,#1e293b,#0f172a)",
              color: current.text_color || "#f8fafc",
            }}
            data-testid={`slide-${index}`}
          >
            <div className="space-y-6">
              {current.title && (
                <h1 className="text-4xl md:text-6xl font-bold leading-tight">{current.title}</h1>
              )}
              {current.content && (
                <p className="text-xl md:text-3xl leading-relaxed whitespace-pre-wrap">{current.content}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <footer className="border-t border-slate-800/50 bg-slate-950/80 backdrop-blur px-6 py-3 flex items-center justify-between">
        <button
          onClick={(e) => { e.stopPropagation(); setIndex((i) => Math.max(i - 1, 0)); }}
          disabled={index === 0}
          className="px-4 py-2 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-30"
          data-testid="prev-slide-btn"
        >
          ← Prev
        </button>
        <a href="/" className="text-slate-500 text-xs hover:text-slate-300">Made with SlideCon</a>
        <button
          onClick={(e) => { e.stopPropagation(); setIndex((i) => Math.min(i + 1, total - 1)); }}
          disabled={index >= total - 1}
          className="px-4 py-2 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-30"
          data-testid="next-slide-btn"
        >
          Next →
        </button>
      </footer>
    </div>
  );
}
